import { memo, useRef } from "react";

import { type TextAreaRef } from "antd/lib/input/TextArea";

import {
  BuiTextArea,
  type IBuiTextAreaProps,
} from "@btnet/blueprint-components";
import { IconX } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import {
  BdsAnalyticsEvent,
  BdsTrack,
  type IBdsTrackingProps,
  type IInputDebouncingContext,
  type KeyOfOrString as TKeyOfOrString,
  trackBdsEvent,
} from "@buildertrend/components";

import { BTText } from "../BTText/BTText";
import { useDebouncedOnChange } from "../hooks/UseDebouncedOnChange";
import { type KeyOfOrString } from "../types/KeyOfOrString.types";
import { ValueDisplay } from "../ValueDisplay/ValueDisplay";
import "./BTTextArea.less";

type OmittedTextAreaProps =
  | "id"
  | "onChange"
  | "onBlur"
  | "placeholder"
  | "value"
  | "maxLength";

export interface IBTTextAreaProps<FormValues>
  extends Omit<IBuiTextAreaProps, OmittedTextAreaProps> {
  id: TKeyOfOrString<FormValues> & string;
  "data-testid": string;
  readOnly?: boolean;

  value: string | undefined;

  /** The max number of characters to allow in the textarea */
  maxLength?: number;

  /** The minimum number of rows to display */
  minRows?: number;

  /** The maximum number of rows to display */
  maxRows?: number;

  /** pass formik setFieldValue */
  onChange: (field: TKeyOfOrString<FormValues>, value: string) => void;

  /** pass formik setFieldTouched */
  onBlur: (
    id: TKeyOfOrString<FormValues>,
    touched: boolean,
    value: string | undefined,
  ) => void;

  /** Ref for the input */
  fieldRef?: React.RefObject<TextAreaRef>;

  /** Allows adjustment/turning off of debouncing on inputs. { useDebouncing: true, inputDelay: 150ms } is the default. */
  debouncingContext?: IInputDebouncingContext;

  /**
   * Blur on change is used to update remaining characters for the text area, but can cause performance issues.
   * @default true
   */
  blurOnChange?: boolean;

  /**
   * Placeholder text for the textarea.
   * @default undefined
   * @example "Enter your text here..."
   */
  placeholder?: string;
}

/**
 * The BTTextArea component is a wrapper around BuiTextArea (the Blueprint-styled
 * antd TextArea). It owns the behavior - debounced change handling, character
 * counting, tracking, and read-only display - while delegating styling to
 * BuiTextArea.
 *
 * When `readOnly` is set, it renders a ValueDisplay instead of an editable textarea.
 * @example
 * <BTTextArea<IFormValues>
 *   id="notes"
 *   data-testid="notes"
 *   value={values.notes}
 *   onChange={setFieldValue}
 *   onBlur={setFieldTouched}
 * />
 * @example // max length example using yup validation
 * myText: yup.string().label("My Text").max(4000, "") // The empty string is used to avoid the default error message and let our component handle the error display.
 */
const BTTextAreaInternal = BdsTrack({ element: "BTTextArea" })(function <
  FormValues = undefined,
>({
  id,
  disabled,
  className,
  onChange,
  onBlur,
  onFocus,
  maxLength,
  value,
  "data-testid": testid,
  readOnly,
  fieldRef,
  rows = 3,
  minRows = 2,
  maxRows = 12,
  debouncingContext,
  blurOnChange = true,
  placeholder,
  tracking,
  trackingEvent,
  ...otherProps
}: IBTTextAreaProps<FormValues> & IBdsTrackingProps) {
  const { debouncedValue, debouncedChange, flush } = useDebouncedOnChange<
    typeof value,
    FormValues
  >(
    value ?? "",
    (field: KeyOfOrString<FormValues>, value: string) => {
      // fire blur event on change so min/max length error appears right away
      if (blurOnChange) {
        onBlur(field, true, value);
      }
      onChange(field, value);
    },
    debouncingContext,
  );

  const refInternal = useRef<TextAreaRef>(null);
  const ref = fieldRef ?? refInternal;

  function onChangeWrapper(event: React.ChangeEvent<HTMLTextAreaElement>) {
    debouncedChange(id, event.target.value);
  }

  function onBlurWrapper() {
    flush();
    onBlur(id, true, debouncedValue);
    trackBdsEvent(tracking, {
      element: "BTTextArea",
      event: trackingEvent ?? BdsAnalyticsEvent.InputBlur,
      testid,
    });
  }

  function onFocusWrapper(event: React.FocusEvent<HTMLTextAreaElement>) {
    if (onFocus) {
      onFocus(event);
    }
    trackBdsEvent(tracking, {
      element: "BTTextArea",
      event: trackingEvent ?? BdsAnalyticsEvent.InputFocus,
      testid,
    });
  }

  let charactersRemaining: JSX.Element | undefined;

  const valueLength = debouncedValue ? debouncedValue.length : 0;
  if (maxLength !== undefined && disabled !== true) {
    const overMaxText = `(${Math.abs(maxLength - valueLength)} too many)`;
    const underMaxText = `(${maxLength - valueLength} remaining)`;

    let charsText = "";
    if (valueLength > 0 && valueLength <= maxLength) {
      charsText = underMaxText;
    } else if (valueLength > maxLength) {
      charsText = overMaxText;
    }

    charactersRemaining = (
      <BTText
        className={cn("bt-text-area--chars-remaining", {
          "bt-text-area--chars-remaining-error": valueLength > maxLength,
        })}
        testid="charsRemaining"
        size="normal-sm"
        text={
          <div className="flex flex-row">
            {valueLength > maxLength && (
              <IconX className="items-center" size="sm" />
            )}
            Maximum {maxLength} characters {charsText}
          </div>
        }
      />
    );
  }

  if (readOnly) {
    return (
      <ValueDisplay
        id={id}
        data-testid={testid}
        value={debouncedValue}
        className={cn(className)}
        isTextArea
      />
    );
  }

  const updatedRows = Math.min(rows, 8); // limit the number of rows to 8, as per design system guidelines

  // note: maxLength is NOT spread on the TextArea by design. We don't want to prevent the user from typing beyond the limit
  return (
    <div className="flex flex-col w-full gap-0.5">
      <BuiTextArea
        id={id}
        name={id}
        data-testid={testid}
        value={debouncedValue}
        onChange={onChangeWrapper}
        onBlur={onBlurWrapper}
        onFocus={onFocusWrapper}
        rows={updatedRows}
        autoSize={{ maxRows, minRows }}
        ref={ref}
        aria-disabled={disabled}
        disabled={disabled}
        placeholder={placeholder}
        className={cn(className, {
          "bui-text-area--error":
            maxLength !== undefined && valueLength > maxLength,
        })}
        {...otherProps}
      />
      {charactersRemaining}
    </div>
  );
});

export const BTTextArea = memo(BTTextAreaInternal) as <FormValues = undefined>(
  props: IBTTextAreaProps<FormValues> & IBdsTrackingProps,
) => JSX.Element;
